import { Component, Inject, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DEFAULT_LANGUAGE } from '@modeso/aumico-lib-core-fe';
import { IGroupEntityDTO } from '@modeso/types__aumico-lib-templates-be';
import Debug from 'debug';
import { Subject } from 'rxjs';
import { GroupFormHelper } from '../../../helpers/group-form.helper';
import { IDynamicKeyFormControl } from '../../../interfaces/dynamic-key-form-control.interface';
import { IGroupFormData } from '../../../interfaces/group-form-data.interface';
import { TemplatesProvider } from '../../../provider/templates.provider';
import { BaseComponent } from '../../base.component';

const debug = Debug('aumico:aumico-lib-templates-fe:GroupFormComponent');

@Component({
    selector: 'templates-group-form',
    templateUrl: 'group-form.component.html',
    styleUrls: ['group-form.component.scss'],
})
export class GroupFormComponent extends BaseComponent implements OnInit {
    public defaultLanguage = DEFAULT_LANGUAGE;

    @Input() languages: string[] = ['en-us', 'de-ch'];
    @Input() public set injectedProviderSetter(injectedProvider: TemplatesProvider) {
        if (injectedProvider) {
            this.templatesProvider = injectedProvider;
        }
    }

    group: IGroupEntityDTO | undefined;
    isEditMode: Boolean = false;

    nameControl: UntypedFormControl | undefined;
    titleFormGroup: UntypedFormGroup | undefined;
    totalNameFormGroup: UntypedFormGroup | undefined;

    groupNameExists: string;

    public isDefault = false;
    public allowGroups = false;
    public allowItems = false;
    public hideTotal = false;
    public notDeletable = false;

    public savePressed = new Subject<IGroupEntityDTO>();
    public updateGroup = new Subject<IGroupEntityDTO>();
    public createGroup = new Subject<IGroupEntityDTO>();

    constructor(
        public override templatesProvider: TemplatesProvider,
        @Inject(MAT_DIALOG_DATA) private data: IGroupFormData,
        public dialogRef: MatDialogRef<GroupFormComponent>,
        private formBuilder: UntypedFormBuilder,
    ) {
        super(templatesProvider);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.initFromControls();
        this.getActiveGroup();
        this.data.groupNameExistsError?.subscribe((errorExists: string) => {
            this.groupNameExists = errorExists;
        });
    }

    private notEmptyValidator(control: UntypedFormControl): ValidationErrors | null {
        if (control.value == null || control.value.toString().trim().length === 0) {
            return { emptyField: true };
        }
        return null;
    }

    private initFromControls(): void {
        this.nameControl = new UntypedFormControl(null, [Validators.required, this.notEmptyValidator]);
        const titleFormControls: IDynamicKeyFormControl = {};
        const totalNameFormControls: IDynamicKeyFormControl = {};

        titleFormControls[DEFAULT_LANGUAGE] = new UntypedFormControl(null, [
            Validators.required,
            this.notEmptyValidator,
        ]);
        totalNameFormControls[DEFAULT_LANGUAGE] = new UntypedFormControl(null, [
            Validators.required,
            this.notEmptyValidator,
        ]);

        this.titleFormGroup = this.formBuilder.group(titleFormControls);
        this.totalNameFormGroup = this.formBuilder.group(totalNameFormControls);
    }

    private getActiveGroup(): void {
        if (this.data.activeGroup == null) {
            return;
        }
        this.group = this.data.activeGroup;
        this.isEditMode = true;

        this.titleFormGroup?.controls[DEFAULT_LANGUAGE].setValue(this.group.title[DEFAULT_LANGUAGE]);
        this.totalNameFormGroup?.controls[DEFAULT_LANGUAGE].setValue(this.group.totalName[DEFAULT_LANGUAGE]);
        this.nameControl?.setValue(this.group.name);

        this.isDefault = this.group.isDefault;
        this.allowGroups = this.group.allowGroups;
        this.allowItems = this.group.allowItems;
        this.hideTotal = this.group.hideTotal;
    }

    public save(): void {
        const groupToBeSaved: IGroupEntityDTO = {
            _id: this.group?._id,
            name: this.nameControl?.value.trim(),
            title: GroupFormHelper.trimEmptySpaces(this.titleFormGroup?.value, DEFAULT_LANGUAGE),
            totalName: GroupFormHelper.trimEmptySpaces(this.totalNameFormGroup?.value, DEFAULT_LANGUAGE),
            allowGroups: this.allowGroups,
            hideTotal: this.hideTotal,
            allowItems: this.allowItems,
            isDefault: this.isDefault,
            notDeletable: this.notDeletable,
        };
        this.savePressed.next(groupToBeSaved);
        if (this.groupNameExists) {
            this.nameControl?.setErrors({ groupNameExists: true });
        } else {
            if (this.isEditMode) {
                this.updateGroup.next(groupToBeSaved);
            } else {
                this.createGroup.next(groupToBeSaved);
            }

            this.closeDialog();
        }
    }

    closeDialog() {
        if (typeof this.dialogRef === 'object' && !(Object.keys(this.dialogRef).length === 0)) {
            this.dialogRef.close();
        }
    }
}
